/**
 * Node visual para criar tarefa do módulo Tarefas via Flow Builder
 *
 * RELEASE: 1.0.0 - 2026-05-31 - Versão inicial
 *
 * Exibe categoria, fase, prioridade, responsável e vínculo (deal/contato)
 * configurados no node `task_create`. Segue o mesmo padrão visual do
 * CrmCreateDealNode (handles, toolbar flutuante, indicador de variável de saída).
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { ListTodo, User, Calendar, Link2, Flag } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { FlowNodeData, TaskCreateConfig } from '@/types/flows';
import { FlowNodeToolbar } from './FlowNodeToolbar';

/** Traduz a prioridade para texto legível em PT-BR */
const getPriorityLabel = (priority?: string): string => {
  switch (priority) {
    case 'low': return 'Baixa';
    case 'high': return 'Alta';
    case 'urgent': return 'Urgente';
    case 'medium':
    default: return 'Média';
  }
};

/** Traduz a unidade do vencimento relativo para texto legível em PT-BR */
const getDueUnitLabel = (unit?: string, value?: number): string => {
  const plural = (value ?? 0) !== 1;
  switch (unit) {
    case 'minutes': return plural ? 'minutos' : 'minuto';
    case 'hours': return plural ? 'horas' : 'hora';
    case 'days':
    default: return plural ? 'dias' : 'dia';
  }
};

/** Traduz o modo de vínculo com deal para texto legível em PT-BR */
const getDealLinkLabel = (mode?: string, varName?: string): string | null => {
  switch (mode) {
    case 'from_trigger': return 'Negócio do gatilho';
    case 'from_variable': return varName ? `Negócio em {{${varName}}}` : 'Negócio em variável';
    default: return null;
  }
};

/** Traduz o modo de atribuição do responsável para texto legível em PT-BR */
const getAssigneeLabel = (
  mode?: string,
  assigneeName?: string,
  assigneeVar?: string,
): string | null => {
  switch (mode) {
    case 'manual': return assigneeName || 'Usuário fixo';
    case 'deal_seller': return 'Vendedor do negócio';
    case 'contact_seller': return 'Vendedor do contato';
    case 'from_variable': return assigneeVar ? `{{${assigneeVar}}}` : 'Variável';
    default: return null;
  }
};

/**
 * Componente visual do node Criar Tarefa
 * Estado "não configurado" é exibido quando faltar categoria ou fase.
 */
export function TaskCreateNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;
  const config = nodeData.config as TaskCreateConfig | undefined;

  const isConfigured = !!(config?.category_id && config?.phase_id);

  // Determina rótulo do título conforme o modo escolhido
  const getTitleLabel = (): string | null => {
    if (!config?.title_mode) return null;
    switch (config.title_mode) {
      case 'static': return config.title_static || 'Texto fixo';
      case 'from_variable': return `{{${config.title_variable || 'variavel'}}}`;
      case 'auto': return 'Automático (categoria + data)';
      default: return null;
    }
  };

  const assigneeLabel = getAssigneeLabel(
    config?.assignee_mode,
    config?.assignee_name,
    config?.assignee_variable,
  );

  const dealLinkLabel = getDealLinkLabel(config?.link_to_deal, config?.deal_variable);

  return (
    <>
      {/* Toolbar flutuante para edição/duplicação/exclusão */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={nodeData.onEdit as (() => void) | undefined}
        onDuplicate={(nodeData as any).onDuplicate as (() => void) | undefined}
        onDelete={nodeData.onDelete as (() => void) | undefined}
        onTestFromHere={(nodeData as any).onTestFromHere as (() => void) | undefined}
        isVisible={selected}
      />

      {/* Handle de entrada (topo) */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-amber-500 !border-2 !border-white"
      />

      {/* Corpo do node */}
      <div
        className={`
          min-w-[220px] max-w-[280px] bg-card rounded-lg border-2 shadow-lg
          ${selected ? 'border-amber-500 ring-2 ring-amber-500/30' : 'border-amber-400'}
          transition-all duration-200
        `}
      >
        {/* Cabeçalho */}
        <div className="flex items-center gap-2 p-3 border-b border-border bg-amber-500/10 rounded-t-lg">
          <div className="p-1.5 rounded bg-amber-500">
            <ListTodo className="h-4 w-4 text-white" />
          </div>
          <span className="font-semibold text-sm truncate">
            {config?.nickname || 'Criar Tarefa'}
          </span>
        </div>

        {/* Conteúdo */}
        <div className="p-3 space-y-2 text-xs">
          {isConfigured ? (
            <>
              {/* Categoria → Fase */}
              <div className="space-y-1">
                <div className="flex items-center gap-1.5">
                  {config.category_color && (
                    <span
                      className="w-2.5 h-2.5 rounded-full inline-block"
                      style={{ backgroundColor: config.category_color }}
                    />
                  )}
                  <span className="text-muted-foreground">Categoria:</span>
                  <span className="font-medium truncate" title={config.category_name}>
                    {config.category_name || '—'}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  {config.phase_color && (
                    <span
                      className="w-2.5 h-2.5 rounded-full inline-block"
                      style={{ backgroundColor: config.phase_color }}
                    />
                  )}
                  <span className="text-muted-foreground">Fase:</span>
                  <span className="font-medium truncate" title={config.phase_name}>
                    {config.phase_name || '—'}
                  </span>
                </div>
                {config.classification_name && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-muted-foreground">Classificação:</span>
                    <span className="font-medium truncate" title={config.classification_name}>
                      {config.classification_name}
                    </span>
                  </div>
                )}
              </div>

              {/* Título */}
              {getTitleLabel() && (
                <div className="flex items-center gap-1.5 pt-1 border-t border-border/50">
                  <span className="text-muted-foreground">Título:</span>
                  <span className="font-medium truncate" title={getTitleLabel() || ''}>
                    {getTitleLabel()}
                  </span>
                </div>
              )}

              {/* Prioridade */}
              <div className="flex items-center gap-1.5">
                <Flag className="h-3 w-3 text-muted-foreground" />
                <span className="text-muted-foreground">Prioridade:</span>
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                  {getPriorityLabel(config.priority)}
                </Badge>
              </div>

              {/* Vencimento */}
              {config.due_date_mode === 'relative' && config.due_date_relative_value ? (
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Vence em:</span>
                  <span className="font-medium">
                    {config.due_date_relative_value}{' '}
                    {getDueUnitLabel(config.due_date_relative_unit, config.due_date_relative_value)}
                  </span>
                </div>
              ) : config.due_date_mode === 'from_variable' && config.due_date_variable ? (
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Vence em:</span>
                  <span className="font-medium truncate">
                    {`{{${config.due_date_variable}}}`}
                  </span>
                </div>
              ) : null}

              {/* Responsável */}
              {assigneeLabel && (
                <div className="flex items-center gap-1.5">
                  <User className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Responsável:</span>
                  <span className="font-medium truncate" title={assigneeLabel}>
                    {assigneeLabel}
                  </span>
                </div>
              )}

              {/* Vínculo com deal */}
              {dealLinkLabel && (
                <div className="flex items-center gap-1.5 pt-1 border-t border-border/50">
                  <Link2 className="h-3 w-3 text-muted-foreground" />
                  <span className="font-medium truncate" title={dealLinkLabel}>
                    {dealLinkLabel}
                  </span>
                </div>
              )}

              {/* Variável de saída (task_id) */}
              {config.output_variable && (
                <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 pt-1 border-t border-border/50">
                  <span>→ Salva ID em:</span>
                  <code className="bg-amber-100 dark:bg-amber-900/30 px-1 rounded text-[10px]">
                    {`{{${config.output_variable}}}`}
                  </code>
                </div>
              )}
            </>
          ) : (
            <p className="text-muted-foreground italic text-center py-2">
              Clique para configurar a tarefa
            </p>
          )}
        </div>
      </div>

      {/* Handle de saída (base) */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !bg-amber-500 !border-2 !border-white"
      />
    </>
  );
}
